import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { Mail } from "lucide-react";

export default function LoginPage() {
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challengeToken, setChallengeToken] = useState(null);
  const [tfaCode, setTfaCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await login(email, password);
      if (r.requires2fa) {
        setChallengeToken(r.challengeToken);
        toast.info("Inserisci il codice a 6 cifre dell'app Authenticator");
      } else {
        toast.success("Accesso effettuato");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err?.friendlyMessage || "Errore durante l'accesso");
    } finally {
      setLoading(false);
    }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await verify2fa(challengeToken, tfaCode);
      if (r.backupCodeUsed) {
        toast.warning(`Codice di riserva usato. Rimanenti: ${r.backupCodesLeft}`);
      } else {
        toast.success("Accesso effettuato");
      }
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.friendlyMessage || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-primary overflow-hidden">
        <div className="absolute inset-0 grain-bg opacity-[0.15]" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-primary-foreground/10 border border-primary-foreground/20 grid place-items-center">
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <div className="font-heading text-lg">TECSend</div>
              <div className="text-[10px] uppercase tracking-[0.25em] opacity-70">
                Newsletter M365
              </div>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] opacity-70 mb-4">
              Spedisci con la tua identità
            </p>
            <h2 className="font-heading text-4xl md:text-5xl leading-[1.1] mb-6">
              Newsletter dal tuo Microsoft 365, senza scorciatoie.
            </h2>
            <p className="text-primary-foreground/70 max-w-md leading-relaxed">
              Importa contatti da CSV, mappa i campi, personalizza con variabili
              e programma invii. Statistiche di apertura e click incluse.
            </p>
          </div>
          <div className="text-xs opacity-60">
            © {new Date().getFullYear()} TECSend
          </div>
        </div>
      </div>

      <div className="grid place-items-center p-8">
        {!challengeToken ? (
          <form
            onSubmit={submit}
            className="w-full max-w-sm space-y-6"
            data-testid="login-form"
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                Accedi
              </p>
              <h1 className="font-heading text-3xl font-medium tracking-tight">
                Bentornato
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Inserisci le tue credenziali per continuare.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  placeholder="nome@azienda.it"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  placeholder="La tua password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Accesso in corso…" : "Accedi"}
            </Button>

            <div className="text-sm text-muted-foreground">
              Non hai un account?{" "}
              <Link
                to="/registrati"
                className="text-primary font-medium hover:underline"
                data-testid="link-register"
              >
                Registrati
              </Link>
            </div>
          </form>
        ) : (
          <form
            onSubmit={submit2fa}
            className="w-full max-w-sm space-y-6"
            data-testid="login-2fa-form"
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">
                Verifica in due passaggi
              </p>
              <h1 className="font-heading text-3xl font-medium tracking-tight">
                Codice di sicurezza
              </h1>
              <p className="text-sm text-muted-foreground mt-2">
                Apri l'app Google Authenticator (o simile) e inserisci il codice a 6 cifre.
                Puoi anche usare un codice di riserva.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="tfa">Codice</Label>
              <Input
                id="tfa"
                type="text"
                required
                autoFocus
                autoComplete="one-time-code"
                inputMode="numeric"
                value={tfaCode}
                onChange={(e) => setTfaCode(e.target.value)}
                data-testid="login-2fa-code"
                placeholder="123 456"
                className="text-center tracking-[0.4em] text-lg font-mono"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={loading || tfaCode.length < 6}
              data-testid="login-2fa-submit"
            >
              {loading ? "Verifica in corso…" : "Verifica e accedi"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setChallengeToken(null);
                setTfaCode("");
              }}
              data-testid="login-2fa-back"
            >
              ← Torna al login
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
